function stripTrailingSlash(value: string): string {
  return value.replace(/\/+$/, "");
}

function publicMarketApiHostname(): string {
  const publicBase = process.env.NEXT_PUBLIC_API_BASE_URL?.trim() || "";
  if (!publicBase) return "";
  try {
    return new URL(publicBase).hostname;
  } catch {
    return "";
  }
}

function isPublicMarketApiHost(hostname: string): boolean {
  const publicHost = publicMarketApiHostname();
  if (publicHost && hostname === publicHost) return true;
  return (
    hostname === "market-api.dev.hireaera.com" ||
    hostname === "market-api.hireaera.com"
  );
}

function isHireAeraUploadPath(url: string): boolean {
  try {
    const parsed = new URL(url);
    if (!parsed.pathname.startsWith("/uploads/")) return false;
    return (
      parsed.hostname === "hireaera.com" ||
      parsed.hostname.endsWith(".hireaera.com")
    );
  } catch {
    return false;
  }
}

function rewriteToServerBase(url: string, serverBase: string): string {
  const parsed = new URL(url);
  return `${serverBase}${parsed.pathname}${parsed.search}${parsed.hash}`;
}

export function isMarketApiPublicUrl(url: string): boolean {
  try {
    return isPublicMarketApiHost(new URL(url.trim()).hostname);
  } catch {
    return false;
  }
}

/** Chat/seller API — browser can reach public HTTPS; Next on this host cannot (hairpin). */
export function isChatApiPublicUrl(url: string): boolean {
  try {
    const host = new URL(url.trim()).hostname;
    if (host === "seller.hireaera.com") return true;
    const chatBase = process.env.NEXT_PUBLIC_CHAT_API_URL?.trim() || "";
    if (!chatBase) return false;
    return host === new URL(chatBase).hostname;
  } catch {
    return false;
  }
}

export function getChatApiServerBase(): string {
  const configured = stripTrailingSlash(
    process.env.CHAT_API_SERVER_URL?.trim() || "",
  );
  if (configured) return configured;
  // Same LAN openresty that serves seller.hireaera.com by Host header.
  return "http://192.168.10.34";
}

export type ProxyUpstream = {
  url: string;
  headers: Record<string, string>;
};

/** Rewrite public market-api / seller URLs to a host this Next process can reach. */
export function resolveProxyUpstream(rawUrl: string): ProxyUpstream {
  if (isChatApiPublicUrl(rawUrl)) {
    const parsed = new URL(rawUrl.trim());
    return {
      url: rewriteToServerBase(rawUrl.trim(), getChatApiServerBase()),
      headers: { Host: parsed.hostname },
    };
  }
  return { url: resolveMarketApiServerUrl(rawUrl), headers: {} };
}

export function resolveMarketApiServerUrl(url: string): string {
  const serverBase = getMarketApiServerBase();
  const publicBase = stripTrailingSlash(
    process.env.NEXT_PUBLIC_API_BASE_URL?.trim() || "",
  );

  const normalizedUrl = url.trim();
  if (publicBase && normalizedUrl.startsWith(publicBase)) {
    return serverBase + normalizedUrl.slice(publicBase.length);
  }

  try {
    const parsed = new URL(normalizedUrl);
    if (isPublicMarketApiHost(parsed.hostname)) {
      return rewriteToServerBase(normalizedUrl, serverBase);
    }
  } catch {
    return url;
  }

  if (isHireAeraUploadPath(normalizedUrl)) {
    const parsed = new URL(normalizedUrl);
    return `${serverBase}${parsed.pathname}${parsed.search}`;
  }

  return url;
}

/**
 * Local openresty often cannot hairpin to public market-api HTTPS, so we
 * rewrite to loopback. Vercel (and similar) can reach the public URL — keep it.
 */
function shouldHairpinPublicMarketApiToLocalhost(): boolean {
  if (process.env.VERCEL) return false;
  if (process.env.MARKET_API_ALLOW_PUBLIC_SERVER_URL === "1") return false;
  return true;
}

function localMarketApiHairpinBase(): string {
  return "http://127.0.0.1:3002";
}

/**
 * Same rewrite as `resolveMarketApiServerUrl`, but keep the original URL
 * when `MARKET_API_SERVER_URL` is missing (client / misconfigured server).
 */
export function tryResolveMarketApiServerUrl(url: string): string {
  try {
    return resolveMarketApiServerUrl(url);
  } catch {
    try {
      const parsed = new URL(url.trim());
      if (
        isPublicMarketApiHost(parsed.hostname) &&
        shouldHairpinPublicMarketApiToLocalhost()
      ) {
        return rewriteToServerBase(url.trim(), localMarketApiHairpinBase());
      }
    } catch {
      // keep original
    }
    return url;
  }
}

/**
 * Server-side market-api base for Next route handlers / proxies.
 * Requires private `MARKET_API_SERVER_URL` — never falls back to
 * `NEXT_PUBLIC_API_BASE_URL` (browser public URL is often unreachable
 * from the Next server process and hides misconfig as silent network errors).
 */
export function getMarketApiServerBase(): string {
  const configured = stripTrailingSlash(
    process.env.MARKET_API_SERVER_URL?.trim() || "",
  );
  if (!configured) {
    throw new Error("MARKET_API_SERVER_URL is not configured");
  }
  try {
    if (
      isPublicMarketApiHost(new URL(configured).hostname) &&
      shouldHairpinPublicMarketApiToLocalhost()
    ) {
      return localMarketApiHairpinBase();
    }
  } catch {
    // keep configured base
  }
  return configured;
}
